import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon, { type IconName } from '../Icon/Icon';
import styles from './PillLink.module.css';

type PillVariant = 'solid' | 'glass' | 'outline';
type PillSize = 'small' | 'medium' | 'large';

interface PillLinkProps {
    href: string;
    children: ReactNode;
    variant?: PillVariant;
    size?: PillSize;
    icon?: IconName;
    className?: string;
}

function isInternalRoute(href: string): boolean {
    return href.startsWith('/');
}

function opensNewTab(href: string): boolean {
    return href.startsWith('http');
}

export default function PillLink({ href, children, variant = 'solid', size = 'medium', icon, className }: PillLinkProps) {
    const { t } = useTranslation();
    const classNames = [styles.pill, styles[variant], styles[size], icon ? styles.withIcon : '', className].filter(Boolean).join(' ');
    const content = (
        <>
            <span className={styles.label}>{children}</span>
            {icon && (
                <span className={styles.iconWrap} data-icon={icon}>
                    <Icon name={icon} className={styles.icon} />
                    <Icon name={icon} className={`${styles.icon} ${styles.iconNext}`} />
                </span>
            )}
        </>
    );

    if (isInternalRoute(href)) {
        return <Link to={href} className={classNames}>{content}</Link>;
    }

    if (!opensNewTab(href)) {
        return <a href={href} className={classNames}>{content}</a>;
    }

    return (
        <a href={href} className={classNames} target="_blank" rel="noopener noreferrer">
            {content}
            <span className="visually-hidden">{t('a11y.opensInNewTab')}</span>
        </a>
    );
}
